import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { AlertTriangle, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Below this the file key is hashed rather than stretched into something
// slow, so a short value is guessable offline by anyone holding the bucket.
// Same number the server enforces on a new one.
const MIN_FILE_KEY = 32;

/**
 * What an administrator needs to be told about the two encryption keys.
 *
 * Two unrelated things sharing one place on the screen, because they are the
 * same question to whoever reads it - is there anything I have to do about the
 * keys - and because a second banner underneath the first is how people learn
 * to scroll past both.
 *
 * Shown only when there is something to say. An installation with nothing
 * pending renders nothing at all.
 */
export default function KeyRotationBanner() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [fileKey, setFileKey] = useState(null);

  const isAdmin = user?.role === "admin";

  const check = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/security/key-status`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      // Silently fail — banner just won't show
    }
    try {
      const res = await axios.get(`${API}/file-key/status`, { withCredentials: true });
      setFileKey(res.data);
    } catch {
      // Same: nothing to say rather than an error on the home page
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;

    // The answer changes only when a key is rotated or the container restarts,
    // which is perhaps twice a year. It used to be polled every thirty
    // seconds, and this banner sits on the home page — the one an admin lands
    // on and leaves open — so that was a few thousand requests a day for a
    // value that is almost always identical to the last one.
    //
    // Mount plus window focus covers both events that can move it, and focus
    // also covers coming back from the rotation page.
    check();
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isAdmin, check]);

  if (!isAdmin) return null;

  const dbNeeds = status?.db_key?.needs_railway_update;
  const fileNeeds = status?.file_key?.needs_railway_update;

  // What the file rotation has to say for itself, if anything. Each case is a
  // different sentence rather than one generic "attention": the point of the
  // card is to say what the next action is, since the whole procedure is one
  // somebody reads about once and then not again for two years.
  //
  // Stated, not addressed to the reader. The button carries the instruction;
  // the sentence above it describes where the procedure stands and what the
  // consequences are, the same register the two rotation pages use.
  let fileCard = null;
  if (fileKey) {
    if (fileKey.step === "copying") {
      fileCard = {
        tone: "busy",
        title: "Rotazione chiave file in corso",
        body: `${fileKey.copied_files} di ${fileKey.total_files} file copiati nella cartella nuova. I file in uso non sono stati toccati.`,
        cta: "Riprendi",
      };
    } else if (fileKey.step === "ready_to_switch") {
      fileCard = {
        tone: "busy",
        title: "Copia dei file completata",
        body: "Tutti i file sono stati copiati con la chiave nuova. Restano la verifica e l'attivazione.",
        cta: "Verifica e attiva",
      };
    } else if (fileKey.step === "switched") {
      fileCard = {
        tone: "good",
        title: "Chiave file ruotata",
        body: "Core legge la copia nuova. La copia vecchia esiste ancora, quindi il ritorno alla chiave precedente resta possibile. L'eliminazione della copia vecchia \u00e8 un passo separato e definitivo.",
        cta: "Apri la procedura",
      };
    } else if (fileKey.key_is_weak) {
      fileCard = {
        tone: "warn",
        title: "La chiave dei file \u00e8 debole",
        body: `\u00c8 lunga ${fileKey.key_length} caratteri invece di ${MIN_FILE_KEY}. Viene trasformata con un hash semplice, quindi un valore corto \u00e8 indovinabile da chi ottenesse il bucket.`,
        cta: "Ruota la chiave",
      };
    }
  }

  if (!dbNeeds && !fileNeeds && !fileCard) return null;

  const tones = {
    warn: "border-amber-300 bg-amber-50 text-amber-900",
    busy: "border-sky-300 bg-sky-50 text-sky-900",
    good: "border-emerald-300 bg-emerald-50 text-emerald-900",
  };

  return (
    <div className="space-y-3 mb-6">
      {(dbNeeds || fileNeeds) && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-5" data-testid="key-rotation-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 space-y-2">
              <p className="font-semibold">{t("keyRotation.actionRequired")}</p>
              {dbNeeds && <p>{t("keyRotation.dbKeyPending")}</p>}
              {fileNeeds && <p>{t("keyRotation.fileKeyPending")}</p>}
              <ol className="list-decimal list-inside space-y-1 text-red-700">
                <li>{t("keyRotation.step1").replace("{KEY}", dbNeeds ? "ENCRYPTION_KEY" : "R2_ENCRYPTION_KEY")}</li>
                <li>{t("keyRotation.step2")}</li>
              </ol>
              <p className="text-red-600 text-xs">{t("keyRotation.autoResolve")}</p>
            </div>
          </div>
        </div>
      )}

      {fileCard && (
        <div className={`rounded-xl border p-5 ${tones[fileCard.tone]}`} data-testid="file-key-banner">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {fileCard.tone === "busy" ? (
                <Loader2 className="w-5 h-5 shrink-0 mt-0.5 animate-spin opacity-70" />
              ) : fileCard.tone === "good" ? (
                <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <p className="font-semibold text-sm">{fileCard.title}</p>
                <p className="text-sm mt-0.5 opacity-90">{fileCard.body}</p>
              </div>
            </div>
            <Link
              to="/settings/file-key"
              data-testid="file-key-banner-link"
              className="inline-flex items-center justify-center gap-1.5 shrink-0 px-3 py-2 rounded-lg bg-white/80 border border-current/20 text-sm font-semibold hover:bg-white transition-colors"
            >
              {fileCard.cta}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
